/**
 * Page Performance console — pure view helpers (no React, no DOM, no network).
 *
 * Everything the screens SAY is worked out here so it can be unit-tested in
 * Node: the URL state, the units on every figure, the waited-time wording,
 * the trend in words, the "In plain words" sentence, why a page has its
 * colour, and the one-line verdict on each browser figure.
 *
 * Two unit rules hold throughout (the spec's named trap):
 *   - FIGURES arrive in milliseconds (times) and fractions 0..1 (rates);
 *   - LIMITS arrive in seconds (times) and percent (rates).
 * `normaliseLimits` converts the limits into the figures' units ONCE, at the
 * boundary, so nothing downstream ever compares 800 (ms) with 2 (s).
 *
 * Every number goes through `formatNumber` and every instant through the
 * shared formatters, so the person's number format, clock and zone apply.
 */
import { formatDateTime, formatNumber, formatPattern, timePattern, datePattern } from '../format.js'
import { titleForPath } from '../path-titles.js'

/* ── Filters, tabs, statuses ───────────────────────────────────────────── */

export const PERIODS = Object.freeze([
  { value: 'today', label: 'Today' },
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
])

export const COMPARES = Object.freeze([
  { value: 'previous', label: 'Compare with previous period', short: 'the previous period' },
  { value: 'yesterday', label: 'Compare with yesterday', short: 'yesterday' },
  { value: 'week', label: 'Compare with previous week', short: 'the previous week' },
  { value: 'month', label: 'Compare with previous month', short: 'the previous month' },
])

export const TABS = Object.freeze(['overview', 'organizations', 'pages', 'detail'])

export const STATUSES = Object.freeze(['healthy', 'warning', 'slow', 'critical'])

export const STATUS_LABEL = Object.freeze({
  healthy: 'Healthy',
  warning: 'Warning',
  slow: 'Slow',
  critical: 'Critical',
})

/** Below this many opens the figures are "thin" (the spec's about-20 rule). */
export const THIN_OPENS = 20

/** The longest route the collector stores (route-key.js MAX_ROUTE_LENGTH). */
const MAX_ROUTE = 300

export function periodLabel(period) {
  return PERIODS.find((p) => p.value === period)?.label ?? 'Today'
}

export function compareShort(compare) {
  return COMPARES.find((c) => c.value === compare)?.short ?? 'the previous period'
}

/**
 * The scope in words, for headings and empty states: "for Acme today",
 * "for all companies in the last 7 days".
 */
export function scopePhrase(companyName, period) {
  const who = companyName ? `for ${companyName}` : 'for all companies'
  return `${who} ${periodPhrase(period)}`
}

/** "today" / "in the last 7 days". */
export function periodPhrase(period) {
  if (period === 'today' || !PERIODS.some((p) => p.value === period)) return 'today'
  return `in the ${periodLabel(period).toLowerCase()}`
}

/**
 * Whether the "users" figure is distinct people (true) or people per day,
 * added up (false). People are hashed per UTC day, so only a window inside
 * one UTC day counts exactly — and a viewer's "Today" outside UTC can span
 * two. The server works this out (`scope.usersExact`); missing → false, so
 * a person-day count is never labelled "distinct people".
 */
export function usersExactFrom(scope) {
  return scope?.usersExact === true
}

/* ── URL state ─────────────────────────────────────────────────────────── */

export const URL_DEFAULTS = Object.freeze({
  organization: 'all',
  period: 'today',
  compare: 'previous',
  tab: 'overview',
  page: null,
  status: '',
})

function readParam(params, key) {
  const v = params.get(key)
  return typeof v === 'string' ? v.trim() : ''
}

function validRoute(route) {
  return typeof route === 'string' && route.startsWith('/') && route.length <= MAX_ROUTE
}

/**
 * Resolve a state into the one the screen can show:
 *   - the Companies tab exists only for "All companies";
 *   - the Page Detail tab exists only while a page is open;
 *   - a page link with no tab lands on Page Detail;
 *   - the status chip belongs to the Pages tab.
 */
export function normaliseUrlState(input = {}) {
  const s = { ...URL_DEFAULTS, ...input }
  const organization = typeof s.organization === 'string' && s.organization.trim() ? s.organization.trim() : 'all'
  const period = PERIODS.some((p) => p.value === s.period) ? s.period : URL_DEFAULTS.period
  const compare = COMPARES.some((c) => c.value === s.compare) ? s.compare : URL_DEFAULTS.compare
  const page = validRoute(s.page) ? s.page : null
  let tab = TABS.includes(s.tab) ? s.tab : null
  if (!tab) tab = page ? 'detail' : 'overview'
  if (tab === 'organizations' && organization !== 'all') tab = 'overview'
  if (tab === 'detail' && !page) tab = 'overview'
  const status = tab === 'pages' && STATUSES.includes(s.status) ? s.status : ''
  return { organization, period, compare, tab, page, status }
}

/** `?organization=…&period=…` (a string or URLSearchParams) → screen state. */
export function parseUrlState(search) {
  const params = search instanceof URLSearchParams ? search : new URLSearchParams(search ?? '')
  const tab = readParam(params, 'tab')
  return normaliseUrlState({
    organization: readParam(params, 'organization') || 'all',
    period: readParam(params, 'period') || URL_DEFAULTS.period,
    compare: readParam(params, 'compare') || URL_DEFAULTS.compare,
    tab: tab || null,
    page: readParam(params, 'page') || null,
    status: readParam(params, 'status'),
  })
}

/** Screen state → query string (defaults left out, stable key order). */
export function buildUrlSearch(state) {
  const s = normaliseUrlState(state)
  const params = new URLSearchParams()
  if (s.organization !== URL_DEFAULTS.organization) params.set('organization', s.organization)
  if (s.period !== URL_DEFAULTS.period) params.set('period', s.period)
  if (s.compare !== URL_DEFAULTS.compare) params.set('compare', s.compare)
  if (s.tab !== URL_DEFAULTS.tab) params.set('tab', s.tab)
  if (s.page) params.set('page', s.page)
  if (s.status) params.set('status', s.status)
  const out = params.toString()
  return out ? `?${out}` : ''
}

/**
 * Apply a change. Moving to any tab other than Page Detail closes the page
 * (the tab disappears when the reader leaves it); opening a page moves to it.
 */
export function nextUrlState(state, patch = {}) {
  const merged = { ...state, ...patch }
  if (patch.page && !patch.tab) merged.tab = 'detail'
  if (patch.tab && patch.tab !== 'detail' && !('page' in patch)) merged.page = null
  if (patch.tab && patch.tab !== 'pages' && !('status' in patch)) merged.status = ''
  return normaliseUrlState(merged)
}

/* ── Limits (seconds / percent → ms / fraction, once) ──────────────────── */

const LIMIT_KEYS = Object.freeze({
  load: ['load', 'loadP95', 'pageLoad', 'pageLoadP95', 'p95', 'loadMs', 'loadP95Ms', 'p95Ms'],
  ttfb: ['ttfb', 'ttfbP95', 'ttfbMs', 'ttfbP95Ms'],
  lcp: ['lcp', 'lcpP75', 'lcpMs', 'lcpP75Ms'],
  errors: ['errors', 'errorRate', 'failedRate', 'failed', 'error', 'errorRatePct'],
})

function levelsFrom(raw) {
  if (Array.isArray(raw) && raw.length >= 3) {
    return { warning: Number(raw[0]), slow: Number(raw[1]), critical: Number(raw[2]), unit: null }
  }
  if (raw && typeof raw === 'object') {
    return {
      warning: Number(raw.warning ?? raw.warn),
      slow: Number(raw.slow),
      critical: Number(raw.critical ?? raw.crit),
      unit: typeof raw.unit === 'string' ? raw.unit.toLowerCase() : null,
    }
  }
  return null
}

function scaled(levels, factor) {
  const out = {
    warning: levels.warning * factor,
    slow: levels.slow * factor,
    critical: levels.critical * factor,
  }
  return [out.warning, out.slow, out.critical].every(Number.isFinite) ? out : null
}

/**
 * The server's `limits.reference` bands for the figures that colour nothing
 * (FCP, CLS, INP — the web-vitals "good / poor" bands), in figure units:
 * FCP and INP in ms (sent in seconds), CLS as its unit-less score.
 */
function normaliseReference(ref) {
  const out = { fcp: null, cls: null, inp: null }
  if (!ref || typeof ref !== 'object') return out
  const band = (b, factor) => {
    const good = Number(b?.good) * factor
    const poor = Number(b?.poor) * factor
    return Number.isFinite(good) && Number.isFinite(poor) ? { good, poor } : null
  }
  const timeFactor = (b) => (String(b?.unit ?? 's').toLowerCase() === 'ms' ? 1 : 1000)
  if (ref.fcpP75) out.fcp = band(ref.fcpP75, timeFactor(ref.fcpP75))
  if (ref.clsP75) out.cls = band(ref.clsP75, 1)
  if (ref.inpP75) out.inp = band(ref.inpP75, timeFactor(ref.inpP75))
  return out
}

/**
 * The server's `limits` object in the figures' own units:
 *   { load, ttfb, lcp } → { warning, slow, critical } in MILLISECONDS
 *   { errors }          → { warning, slow, critical } as FRACTIONS (0..1)
 * A metric the server did not send is `null` — the screens then say the
 * limit is unknown rather than inventing one.
 *
 * Unit rule: a time limit is in seconds unless its key ends in `Ms` or it
 * says `unit: 'ms'`; a rate limit is in percent unless it says
 * `unit: 'fraction'`.
 */
export function normaliseLimits(raw) {
  const out = { load: null, ttfb: null, lcp: null, errors: null, reference: normaliseReference(raw?.reference) }
  if (!raw || typeof raw !== 'object') return out
  for (const metric of Object.keys(LIMIT_KEYS)) {
    const key = LIMIT_KEYS[metric].find((k) => raw[k] != null)
    if (!key) continue
    const levels = levelsFrom(raw[key])
    if (!levels) continue
    if (metric === 'errors') {
      out.errors = scaled(levels, levels.unit === 'fraction' ? 1 : 0.01)
    } else {
      const inMs = levels.unit === 'ms' || /Ms$/.test(key)
      out[metric] = scaled(levels, inMs ? 1 : 1000)
    }
  }
  return out
}

/** The level a figure reaches against its limits (same units). */
export function levelFor(value, levels) {
  if (!levels || value == null || !Number.isFinite(value)) return null
  if (value > levels.critical) return 'critical'
  if (value > levels.slow) return 'slow'
  if (value > levels.warning) return 'warning'
  return 'healthy'
}

/* ── Units on screen ───────────────────────────────────────────────────── */

const isNum = (v) => typeof v === 'number' && Number.isFinite(v)

/** A load time: "346 ms" under a second, "1.0 s" above. "—" when missing. */
export function formatMs(ms) {
  if (!isNum(ms)) return '—'
  if (ms < 1000) return `${formatNumber(Math.round(ms))} ms`
  return `${formatNumber(ms / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`
}

/** A limit, always in seconds as the limits are typed: "2.0 s", "0.8 s". */
export function formatLimitSeconds(ms) {
  if (!isNum(ms)) return '—'
  return `${formatNumber(ms / 1000, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`
}

/**
 * Time waited in total. A four-digit second count is not a quantity anybody
 * weighs, so a minute or more reads "2 min 17 s" and an hour or more
 * "1 h 05 min".
 */
export function formatWaited(ms) {
  if (!isNum(ms) || ms < 0) return '—'
  const totalSec = Math.round(ms / 1000)
  if (totalSec < 60) return formatMs(ms)
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60)
    const s = totalSec % 60
    return `${formatNumber(m)} min ${formatNumber(s)} s`
  }
  const totalMin = Math.round(totalSec / 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  return `${formatNumber(h)} h ${String(m).padStart(2, '0')} min`
}

/** A rate (fraction 0..1) as a percentage: "4.8%", "0%". */
export function formatPercent(fraction) {
  if (!isNum(fraction)) return '—'
  return `${formatNumber(fraction * 100, { maximumFractionDigits: 1 })}%`
}

/** A limit given as a fraction, as a percentage: "2%", "3.5%". */
export function formatLimitPercent(fraction) {
  return formatPercent(fraction)
}

/** A reply size: "512 B", "5.0 KB", "1.2 MB". */
export function formatBytes(bytes) {
  if (!isNum(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${formatNumber(Math.round(bytes))} B`
  if (bytes < 1024 * 1024) return `${formatNumber(bytes / 1024, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} KB`
  return `${formatNumber(bytes / (1024 * 1024), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`
}

/** A count with its noun: "1 open", "84 opens". */
export function countOf(n, singular, plural = `${singular}s`) {
  const v = isNum(n) ? n : 0
  return `${formatNumber(v)} ${v === 1 ? singular : plural}`
}

/** "about 8 of every 100 opens" — a rate a person can weigh. */
export function perHundredText(fraction) {
  if (!isNum(fraction)) return 'not known'
  const per = fraction * 100
  if (per === 0) return 'none of the opens'
  if (per < 1) return 'fewer than 1 of every 100 opens'
  return `about ${formatNumber(Math.round(per))} of every 100 opens`
}

/** "limit 2 of 100" for a rate limit given as a fraction. */
export function limitPerHundredText(fraction) {
  if (!isNum(fraction)) return 'limit not known'
  return `limit ${formatNumber(fraction * 100, { maximumFractionDigits: 1 })} of 100`
}

/** "2.0 s / 3.0 s / 4.0 s" — the three levels of a time limit. */
export function limitTriple(levels) {
  if (!levels) return 'limits not known'
  return `${formatLimitSeconds(levels.warning)} / ${formatLimitSeconds(levels.slow)} / ${formatLimitSeconds(levels.critical)}`
}

/* ── Compare deltas ────────────────────────────────────────────────────── */

/**
 * The change against the compared window. `good` says whether the move is an
 * improvement (lower is better for times and failures, higher for loads is
 * neutral → `good: null`).
 */
export function deltaOf(v, prev, { lowerIsBetter = true, neutral = false } = {}) {
  if (!isNum(v) || !isNum(prev)) return null
  if (prev === 0) {
    if (v === 0) return { dir: 'flat', pct: 0, good: null, text: 'no change' }
    return { dir: 'up', pct: null, good: neutral ? null : !lowerIsBetter, text: '▲ new' }
  }
  const pct = ((v - prev) / prev) * 100
  const rounded = Math.round(Math.abs(pct))
  if (rounded === 0) return { dir: 'flat', pct: 0, good: null, text: 'no change' }
  const dir = pct > 0 ? 'up' : 'down'
  const good = neutral ? null : (dir === 'down') === lowerIsBetter
  return { dir, pct: rounded, good, text: `${dir === 'up' ? '▲' : '▼'} ${formatNumber(rounded)}%` }
}

/* ── Page names ────────────────────────────────────────────────────────── */

/**
 * The app's own title for a measured route, using the same map as the
 * browser tab. `titleForPath` reads `:id` / `:param` segments as ids, so
 * `/app/spaces/:id/lists/:id` reads "Space List" — the tab's own name.
 */
export function pageNameForRoute(route) {
  if (typeof route !== 'string' || !route) return 'Unknown page'
  if (route === '(other)') return 'Other pages'
  if (route === '/') return 'Home'
  return titleForPath(route) ?? route
}

/* ── Trend in words ────────────────────────────────────────────────────── */

function hasOpens(p) {
  return p && isNum(p.p95Ms) && (p.loads == null || p.loads > 0)
}

/** "hour" for Today / 24 h, "day" for longer periods. */
export function slotUnit(slot) {
  return slot === 'day' ? 'day' : 'hour'
}

/** The trend column's header note — never the word "bucket". */
export function trendHeaderNote(slot) {
  const unit = slotUnit(slot)
  return `P95 per ${unit} · needs opens in 2+ ${unit}s`
}

/**
 * What the little line can say. A line needs opens in two slots; with one
 * it says why there is no line, with none it says so.
 */
export function trendInWords(trend, slot) {
  const unit = slotUnit(slot)
  const points = (Array.isArray(trend) ? trend : []).filter(hasOpens)
  if (points.length === 0) return { kind: 'none', text: 'No opens in this period', sub: '', points }
  if (points.length === 1) {
    return {
      kind: 'one',
      text: 'No trend yet',
      sub: `opened in 1 ${unit} only — the line needs 2+`,
      points,
    }
  }
  return {
    kind: 'line',
    text: `P95 across ${formatNumber(points.length)} ${unit}s`,
    sub: '',
    points,
  }
}

/** SVG path for a sparkline of `values` inside `w` × `h` (1 px breathing). */
export function sparkPath(values, w, h) {
  const vals = (values ?? []).filter(isNum)
  if (vals.length < 2) return ''
  const min = Math.min(...vals)
  const max = Math.max(...vals)
  const range = max - min || 1
  const step = (w - 2) / (vals.length - 1)
  return vals
    .map((v, i) => {
      const x = 1 + i * step
      const y = h - 1 - ((v - min) / range) * (h - 2)
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')
}

/* ── Times on screen ───────────────────────────────────────────────────── */

/** "18 min ago" / "6 h ago" / "3 days ago"; older → the full date and time. */
export function relativeTime(iso, now = Date.now()) {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (!Number.isFinite(t)) return '—'
  const min = Math.round((now - t) / 60_000)
  if (min < 1) return 'just now'
  if (min < 60) return `${formatNumber(min)} min ago`
  const h = Math.round(min / 60)
  if (h < 24) return `${formatNumber(h)} h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${formatNumber(d)} ${d === 1 ? 'day' : 'days'} ago`
  return formatDateTime(iso)
}

/**
 * A chart slot's label. Hours are instants (the person's zone and clock);
 * days are UTC calendar days, so only the date part is read — never shifted.
 */
export function slotLabel(t, slot) {
  if (!t) return ''
  if (slot === 'day') {
    const day = String(t).slice(0, 10)
    return formatPattern(day, datePattern().startsWith('MM') ? 'MMM d' : 'd MMM') || day
  }
  return formatPattern(t, timePattern())
}

/** Long label for a tooltip: "06/10/2026 04:00 PM" / "6 Oct (UTC day)". */
export function slotLongLabel(t, slot) {
  if (!t) return ''
  if (slot === 'day') return `${slotLabel(t, slot)} (UTC day)`
  return formatDateTime(t)
}

/* ── "In plain words" ──────────────────────────────────────────────────── */

/**
 * The six load-time figures read as sentences, with the real numbers.
 * Returns segments: plain strings and `{ b: '…' }` for the bold figures, so
 * the screen can emphasise them without parsing.
 */
export function plainWordsSegments({ loads, users, p50Ms, p75Ms, p95Ms, p99Ms, avgMs, usersExact = false, abandoned } = {}) {
  if (!isNum(loads) || loads <= 0) {
    if (isNum(abandoned) && abandoned > 0) {
      return [
        'this page was opened ',
        { b: countOf(abandoned, 'time') },
        ` in the period, but ${abandoned === 1 ? 'that open was' : 'every open was'} left before the page finished loading — so there is no load time to read yet.`,
      ]
    }
    return ['this page was not opened in this period.']
  }
  const who = isNum(users) && users > 0
    ? ` by ${countOf(users, 'person', 'people')}${usersExact ? '' : ' (counted once per day)'}`
    : ''
  if (loads === 1) {
    return [
      'this page was opened ',
      { b: 'once' },
      `${who} in the period and took `,
      { b: formatMs(avgMs ?? p50Ms) },
      ' — with a single open, the typical, the slowest and the average are all that one time.',
    ]
  }
  const out = [
    'this page was opened ',
    { b: countOf(loads, 'time') },
    `${who} in the period. Put the ${formatNumber(loads)} opens in a line from fastest to slowest: the middle one is `,
    { b: formatMs(p50Ms) },
    ' (typical), 3 of 4 are within ',
    { b: formatMs(p75Ms) },
    ', 95 of 100 are within ',
    { b: formatMs(p95Ms) },
  ]
  if (loads >= 100) out.push(', and 99 of 100 are within ', { b: formatMs(p99Ms) })
  else out.push(', and the slowest is ', { b: formatMs(p99Ms) })
  out.push(`. Adding the times up and dividing by ${formatNumber(loads)} gives the average, `, { b: formatMs(avgMs) })
  if (isNum(avgMs) && isNum(p50Ms) && avgMs > p50Ms * 1.15) {
    out.push(' — pulled up by the few slow opens, which is why it is higher than the typical open.')
  } else if (isNum(avgMs) && isNum(p50Ms) && avgMs < p50Ms * 0.85) {
    out.push(' — pulled down by a few very fast opens, which is why it is lower than the typical open.')
  } else {
    out.push(' — close to the typical open, so the opens are fairly even.')
  }
  return out
}

/** The same sentence as one string (exports, tests, screen readers). */
export function plainWordsText(figures) {
  return plainWordsSegments(figures)
    .map((s) => (typeof s === 'string' ? s : s.b))
    .join('')
}

/** The thin-data warning, or null from about 20 opens. */
export function thinDataText(loads) {
  if (!isNum(loads) || loads <= 0 || loads >= THIN_OPENS) return null
  const n = countOf(loads, 'open')
  return `Only ${n} — with so few, "typical" and "slowest" are simply those opens. The figures start to mean something from about ${THIN_OPENS} opens.`
}

/* ── Why the page has its colour ───────────────────────────────────────── */

/** Which of the four measures a server metric name refers to. */
export function metricKind(metric) {
  const m = String(metric ?? '').toLowerCase()
  if (m.includes('ttfb')) return 'ttfb'
  if (m.includes('lcp')) return 'lcp'
  if (m.includes('err') || m.includes('fail')) return 'errors'
  if (m) return 'load'
  return null
}

const MEASURE_PHRASE = Object.freeze({
  load: 'the slowest 5 of 100 opens (P95)',
  ttfb: 'the wait for the first byte on full loads (TTFB P95)',
  lcp: 'the main content appearing on full loads (LCP P75)',
  errors: 'opens with a failed request',
})

function formatFigure(kind, value) {
  return kind === 'errors' ? formatPercent(value) : formatMs(value)
}

function formatLimit(kind, value) {
  return kind === 'errors' ? formatPercent(value) : formatLimitSeconds(value)
}

/**
 * Bring a server `statusReason.limit` into the figure's units when the
 * normalised limits are not available. The reason names its own unit
 * (`limitUnit`: 's' | 'ms' | '%' | 'fraction'); without one, the documented
 * units apply (seconds for times, percent for rates).
 */
function reasonLimitInFigureUnits(kind, limit, unit) {
  if (!isNum(limit)) return null
  const u = String(unit ?? '').toLowerCase()
  if (kind === 'errors') return u === 'fraction' ? limit : limit / 100
  return u === 'ms' ? limit : limit * 1000
}

/**
 * Why a page (or company) has the colour it has — naming the measure
 * that crossed its limit rather than repeating the status word.
 *
 * `statusReason` is `{ metric, level, value, valueUnit, limit, limitUnit }`
 * from the server (value in figure units, limit as typed); `limits` are
 * the normalised limits; `p95Ms` lets a healthy page say how far inside it is.
 */
export function statusReasonText({ status, statusReason, limits, p95Ms } = {}) {
  const label = STATUS_LABEL[status]
  if (!label) return 'Its colour has not been worked out on this server yet.'
  const norm = limits ?? {}
  if (status === 'healthy') {
    const warn = norm.load?.warning
    if (isNum(p95Ms) && isNum(warn)) {
      return `The Healthy colour comes from every measure being inside its limit — the slowest 5 of 100 opens (P95) took ${formatMs(p95Ms)}, under the ${formatLimitSeconds(warn)} warning limit.`
    }
    return 'The Healthy colour comes from every measure being inside its limit.'
  }
  const kind = metricKind(statusReason?.metric)
  if (!kind) {
    return `The ${label} colour is the worst of its four measures (P95 load time, TTFB, LCP and failed requests) against their limits.`
  }
  const value = statusReason?.value
  const crossed = STATUS_LABEL[statusReason?.level] ? statusReason.level : status
  const fromLimits = norm[kind]?.[crossed]
  const limit = isNum(fromLimits)
    ? fromLimits
    : reasonLimitInFigureUnits(kind, statusReason?.limit, statusReason?.limitUnit)
  const figure = isNum(value) ? formatFigure(kind, value) : null
  const limitText = isNum(limit) ? `the ${formatLimit(kind, limit)} ${label} limit` : `its ${label} limit`
  if (kind === 'errors') {
    return figure
      ? `The ${label} colour comes from ${MEASURE_PHRASE.errors}: ${figure} of the opens had one — over ${limitText}.`
      : `The ${label} colour comes from ${MEASURE_PHRASE.errors} — over ${limitText}.`
  }
  return figure
    ? `The ${label} colour comes from ${MEASURE_PHRASE[kind]}: ${figure} — over ${limitText}.`
    : `The ${label} colour comes from ${MEASURE_PHRASE[kind]} — over ${limitText}.`
}

/* ── Browser figures: one-line verdicts ────────────────────────────────── */

/*
 * FCP, CLS and INP have no limit (they are informational); their verdict
 * uses the web-vitals reference bands the server sends in
 * `limits.reference` (normalised into `limits.reference`). They are NOT
 * limits and colour nothing. No band sent → the verdict says so.
 */
function bandVerdict(value, band) {
  if (!band) return 'for context — no reference band sent'
  if (value <= band.good) return 'good'
  if (value <= band.poor) return 'needs improvement'
  return 'poor'
}

const LEVEL_WORD = Object.freeze({
  healthy: 'healthy',
  warning: 'over the warning limit',
  slow: 'over the slow limit',
  critical: 'over the critical limit',
})

/**
 * `{ text, level }` for one browser tile. `level` is a status for the
 * limited figures (TTFB, LCP, failed requests) and null for the others.
 * `serverLevel` (Page Detail's `browser.levels`) wins when the server sent it.
 */
export function browserVerdict(kind, value, limits, serverLevel) {
  const norm = limits ?? {}
  const given = STATUS_LABEL[serverLevel] ? serverLevel : null
  if (kind === 'ttfb' || kind === 'lcp') {
    if (!isNum(value)) return { text: 'not measured — no full loads in this period', level: null }
    const levels = norm[kind]
    const level = given ?? levelFor(value, levels)
    if (!levels || !level) return { text: 'limit not known', level }
    return { text: `${LEVEL_WORD[level]} · > ${formatLimitSeconds(levels.warning)} warns`, level }
  }
  if (kind === 'errors') {
    if (!isNum(value)) return { text: 'no opens in this period', level: null }
    const levels = norm.errors
    const level = given ?? levelFor(value, levels)
    if (!levels || !level) return { text: 'limit not known', level }
    return { text: `${LEVEL_WORD[level]} · > ${formatPercent(levels.warning)} warns`, level }
  }
  if (kind === 'fcp') {
    if (!isNum(value)) return { text: 'not measured — no full loads in this period', level: null }
    return { text: `first paint · ${bandVerdict(value, norm.reference?.fcp)}`, level: null }
  }
  if (kind === 'cls') {
    if (!isNum(value)) return { text: 'not measured yet', level: null }
    return { text: bandVerdict(value, norm.reference?.cls), level: null }
  }
  if (kind === 'inp') {
    if (!isNum(value)) return { text: 'no clicks or key presses recorded', level: null }
    return { text: bandVerdict(value, norm.reference?.inp), level: null }
  }
  return { text: '', level: null }
}

/** CLS is a score, not a time: "0.032". */
export function formatCls(v) {
  if (!isNum(v)) return '—'
  return formatNumber(v, { minimumFractionDigits: 3, maximumFractionDigits: 3 })
}

/* ── "How the load times are worked out" picture ──────────────────────── */

function lerp(a, b, t) {
  return a + (b - a) * t
}

/**
 * Bars for the picture of the opens, fastest to slowest.
 *   - With the page's real opens (`opens`, sorted, Page Detail on short
 *     periods) the bars are those opens, sampled down to `max`.
 *   - Without them the bars are an ILLUSTRATION drawn through the figures
 *     (typical, P95, P99) — `real: false`, and the window says so.
 * Returns `{ bars, real, marks: { p50, p95, p99 } }` (marks are bar indexes).
 */
export function opensPicture({ opens, loads, p50Ms, p95Ms, p99Ms } = {}, max = 60) {
  const real = Array.isArray(opens) ? opens.filter(isNum).sort((a, b) => a - b) : []
  let bars
  if (real.length > 0) {
    if (real.length <= max) bars = real
    else {
      bars = []
      for (let i = 0; i < max; i += 1) {
        bars.push(real[Math.round((i * (real.length - 1)) / (max - 1))])
      }
    }
  } else {
    const n = Math.max(1, Math.min(isNum(loads) ? Math.round(loads) : 20, max))
    const p50 = isNum(p50Ms) ? p50Ms : 0
    const p95 = isNum(p95Ms) ? p95Ms : p50
    const p99 = isNum(p99Ms) ? p99Ms : p95
    const knots = [
      [0, p50 * 0.5],
      [0.5, p50],
      [0.95, p95],
      [0.99, p99],
      [1, p99],
    ]
    bars = []
    for (let i = 0; i < n; i += 1) {
      const q = n === 1 ? 0.5 : i / (n - 1)
      let k = 0
      while (k < knots.length - 2 && q > knots[k + 1][0]) k += 1
      const [q0, v0] = knots[k]
      const [q1, v1] = knots[k + 1]
      bars.push(lerp(v0, v1, q1 === q0 ? 0 : (q - q0) / (q1 - q0)))
    }
  }
  const at = (q) => Math.min(bars.length - 1, Math.max(0, Math.ceil(q * bars.length) - 1))
  return { bars, real: real.length > 0, marks: { p50: at(0.5), p95: at(0.95), p99: at(0.99) } }
}
